import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  ComplianceExclusionReason,
  EmployeeDTO,
  EmployeeStatus,
  EmployeeType,
} from '@deepguide-ai/types'
import { toast } from 'sonner'
import { EXCLUSION_REASON_OPTIONS } from './exclusion'
import { trpc } from '@/lib/trpc'
import { ManagerSelect } from './ManagerSelect'
import { Button } from '@deepguide-ai/ui/button'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Textarea } from '@deepguide-ai/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'

interface EditEmployeeDialogProps {
  employee: EmployeeDTO | null
  open: boolean
  onOpenChange: (open: boolean) => void
  readOnly?: boolean
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  /** Free-form newline / comma separated list, normalized on submit. */
  alternativeEmails: string
  title: string
  department: string
  manager: string
  /** Structured link to the manager's Employee row (null when free text). */
  managerEmployeeId: string | null
  location: string
  ndaSignedAt: Date | undefined
  jobDescription: string
  type: EmployeeType
  status: EmployeeStatus
  startDate: Date | undefined
  endDate: Date | undefined
  /** RFC-180 M4 G8.3 — program-level role assignment. */
  roleId: string | null
  /** Compliance-duty exclusion — null = not excluded. */
  complianceExclusionReason: ComplianceExclusionReason | null
  complianceExclusionNote: string
}

const defaultForm: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  alternativeEmails: '',
  title: '',
  department: '',
  manager: '',
  managerEmployeeId: null,
  location: '',
  ndaSignedAt: undefined,
  jobDescription: '',
  type: 'FULL_TIME',
  status: 'ACTIVE',
  startDate: undefined,
  endDate: undefined,
  roleId: null,
  complianceExclusionReason: null,
  complianceExclusionNote: '',
}

/** Split a free-form newline/comma input into a normalized email list. */
function parseAlternativeEmails(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0)
}

function toFormData(employee: EmployeeDTO | null): FormData {
  if (!employee) {
    return defaultForm
  }

  return {
    firstName: employee.firstName,
    lastName: employee.lastName,
    email: employee.email,
    alternativeEmails: (employee.alternativeEmails ?? []).join('\n'),
    title: employee.title ?? '',
    department: employee.department ?? '',
    manager: employee.manager ?? '',
    managerEmployeeId: employee.managerEmployeeId ?? null,
    location: employee.location ?? '',
    ndaSignedAt: employee.ndaSignedAt ?? undefined,
    jobDescription: employee.jobDescription ?? '',
    type: employee.type,
    status: employee.status,
    startDate: employee.startDate ?? undefined,
    endDate: employee.endDate ?? undefined,
    roleId: employee.roleId ?? null,
    complianceExclusionReason: employee.complianceExclusionReason ?? null,
    complianceExclusionNote: employee.complianceExclusionNote ?? '',
  }
}

export function EditEmployeeDialog({
  employee,
  open,
  onOpenChange,
  readOnly = false,
}: EditEmployeeDialogProps) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<FormData>(defaultForm)

  // Access-review role picker hidden until Access Reviews ships (RFC-180 M4).
  // `form.roleId` is still initialized + submitted below so existing assignments
  // are preserved untouched; re-add the roleDefinition.list query + the field to
  // restore it.

  useEffect(() => {
    if (open) {
      setForm(toFormData(employee))
    }
  }, [employee, open])

  const updateMutation = useMutation({
    ...trpc.employee.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.employee.list.queryKey() })
      queryClient.invalidateQueries({
        queryKey: trpc.employee.stats.queryKey(),
      })
      queryClient.invalidateQueries({ queryKey: trpc.employee.orgChart.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.employee.get.queryKey() })
      toast.success('Employee updated')
      onOpenChange(false)
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Failed to update employee')
    },
  })

  const set =
    <K extends keyof FormData>(key: K) =>
    (value: FormData[K]) =>
      setForm((current) => ({ ...current, [key]: value }))

  const handleSubmit = () => {
    if (readOnly || !employee) {
      return
    }
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast.error('First name, last name, and email are required')
      return
    }

    updateMutation.mutate({
      id: employee.id,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      alternativeEmails: parseAlternativeEmails(form.alternativeEmails),
      title: form.title.trim() || null,
      department: form.department.trim() || null,
      manager: form.manager.trim() || null,
      managerEmployeeId: form.managerEmployeeId,
      location: form.location.trim() || null,
      ndaSignedAt: form.ndaSignedAt ?? null,
      jobDescription: form.jobDescription.trim() || null,
      type: form.type,
      status: form.status,
      startDate: form.startDate ?? null,
      endDate: form.endDate ?? null,
      roleId: form.roleId,
      complianceExclusionReason: form.complianceExclusionReason,
      // Only meaningful when excluded; clear the note when re-including.
      complianceExclusionNote: form.complianceExclusionReason
        ? form.complianceExclusionNote.trim() || null
        : null,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[520px]'>
        <DialogHeader>
          <DialogTitle>Edit Employee</DialogTitle>
          <DialogDescription>
            Update employee directory and people evidence fields.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea viewClassName='max-h-[60dvh] px-6 h-full [&>div]:!block'>
          <div className='grid grid-cols-2 gap-4 py-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-firstName'>First Name *</Label>
              <Input
                id='edit-emp-firstName'
                value={form.firstName}
                disabled={readOnly}
                onChange={(e) => set('firstName')(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-lastName'>Last Name *</Label>
              <Input
                id='edit-emp-lastName'
                value={form.lastName}
                disabled={readOnly}
                onChange={(e) => set('lastName')(e.target.value)}
              />
            </div>

            <div className='col-span-2 space-y-1.5'>
              <Label htmlFor='edit-emp-email'>Email *</Label>
              <Input
                id='edit-emp-email'
                type='email'
                value={form.email}
                disabled={readOnly}
                onChange={(e) => set('email')(e.target.value)}
              />
            </div>

            <div className='col-span-2 space-y-1.5'>
              <Label htmlFor='edit-emp-alt-emails'>Other emails</Label>
              <Textarea
                id='edit-emp-alt-emails'
                value={form.alternativeEmails}
                disabled={readOnly}
                onChange={(e) => set('alternativeEmails')(e.target.value)}
                placeholder='alice@aws.acme.com&#10;alice.github@gmail.com'
                rows={3}
              />
              <p className='text-[10px] text-text-subtle'>
                One per line, or comma-separated. Used by access reviews to
                match this person across systems (Azure, AWS, GitHub, etc.).
              </p>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-title'>Job Title</Label>
              <Input
                id='edit-emp-title'
                value={form.title}
                disabled={readOnly}
                onChange={(e) => set('title')(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-department'>Department</Label>
              <Input
                id='edit-emp-department'
                value={form.department}
                disabled={readOnly}
                onChange={(e) => set('department')(e.target.value)}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-manager'>Manager</Label>
              <ManagerSelect
                id='edit-emp-manager'
                excludeEmployeeId={employee?.id}
                managerEmployeeId={form.managerEmployeeId}
                managerName={form.manager}
                disabled={readOnly}
                onChange={({ managerEmployeeId, manager }) =>
                  setForm((current) => ({
                    ...current,
                    managerEmployeeId,
                    manager: manager ?? '',
                  }))
                }
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-location'>Location</Label>
              <Input
                id='edit-emp-location'
                value={form.location}
                disabled={readOnly}
                onChange={(e) => set('location')(e.target.value)}
              />
            </div>

            <p className='col-span-2 text-sm text-muted-foreground'>
              Manage completion evidence in People → Background checks after saving employee details.
            </p>
            <div className='space-y-1.5'>
              <Label>NDA Signed At</Label>
              <DatePicker
                captionLayout='dropdown'
                startMonth={new Date(1900, 0, 1)}
                value={form.ndaSignedAt}
                disabled={readOnly}
                onChange={(date) => set('ndaSignedAt')(date ?? undefined)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Onboard date</Label>
              <DatePicker
                captionLayout='dropdown'
                startMonth={new Date(1900, 0, 1)}
                value={form.startDate}
                disabled={readOnly}
                onChange={(date) => set('startDate')(date ?? undefined)}
              />
            </div>

            <div className='space-y-1.5'>
              <Label>Offboard date</Label>
              <DatePicker
                captionLayout='dropdown'
                startMonth={new Date(1900, 0, 1)}
                value={form.endDate}
                disabled={readOnly}
                onChange={(date) => set('endDate')(date ?? undefined)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-type'>Employment Type</Label>
              <Select value={form.type} onValueChange={set('type')} disabled={readOnly}>
                <SelectTrigger id='edit-emp-type' fullWidth>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='FULL_TIME'>Full Time</SelectItem>
                  <SelectItem value='PART_TIME'>Part Time</SelectItem>
                  <SelectItem value='CONTRACTOR'>Contractor</SelectItem>
                  <SelectItem value='INTERN'>Intern</SelectItem>
                  <SelectItem value='TEMP'>Temp</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='edit-emp-status'>Status</Label>
              <Select value={form.status} onValueChange={set('status')} disabled={readOnly}>
                <SelectTrigger id='edit-emp-status' fullWidth>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='ACTIVE'>Active</SelectItem>
                  <SelectItem value='ON_LEAVE'>On Leave</SelectItem>
                  <SelectItem value='TERMINATED'>Terminated</SelectItem>
                  <SelectItem value='CONTRACTOR'>Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='col-span-2 space-y-1.5'>
              <Label htmlFor='edit-emp-job-description'>Job Description</Label>
              <Input
                id='edit-emp-job-description'
                value={form.jobDescription}
                disabled={readOnly}
                onChange={(e) => set('jobDescription')(e.target.value)}
              />
            </div>

            {/* Compliance-duty exclusion — service/test accounts etc. When set,
                this person is dropped from coverage stats, training, policy-ack,
                and reports. */}
            <div className='col-span-2 space-y-1.5'>
              <Label htmlFor='edit-emp-exclusion'>Compliance exclusion</Label>
              <Select
                value={form.complianceExclusionReason ?? '__none__'}
                disabled={readOnly}
                onValueChange={(v) =>
                  set('complianceExclusionReason')(
                    v === '__none__' ? null : (v as ComplianceExclusionReason),
                  )
                }
              >
                <SelectTrigger id='edit-emp-exclusion' fullWidth>
                  <SelectValue placeholder='Not excluded' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='__none__'>
                    Not excluded (counts toward compliance)
                  </SelectItem>
                  {EXCLUSION_REASON_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.complianceExclusionReason ? (
                <Textarea
                  value={form.complianceExclusionNote}
                  disabled={readOnly}
                  onChange={(e) =>
                    set('complianceExclusionNote')(e.target.value)
                  }
                  placeholder='Optional note (e.g. CI service account for deploys)'
                  rows={2}
                />
              ) : null}
              <p className='text-[10px] text-text-subtle'>
                Excluded people (service/test accounts) are removed from coverage
                stats, training, policy acknowledgments, and reports.
              </p>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!readOnly && (
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
